import { Label, Text } from '@/_components';
import { SectionHeading } from '@/_containers';
import { sourceLabels, type SupportTicketSummary } from '@/_containers/support/shared/schemas/support-query-schema';
import { capitalize, dateTime, nameResolver } from '@/_shared';

import { Header } from '../utility-components';

const TicketInformationItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div>
      <Label>{label}</Label>
      <Text as="p" className="text-accent dark:text-white">
        {value}
      </Text>
    </div>
  );
};

export function TicketInformationSection({
  ticket,
  formatDueDate,
}: {
  ticket: SupportTicketSummary;
  formatDueDate: (dateStr: string) => { formatted: string; isOverdue: boolean };
}) {
  return (
    <div className="space-y-4">
      <SectionHeading text="Ticket Information" />

      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-6">
        {/* Basic Ticket Information */}
        <TicketInformationItem label="Created" value={dateTime.format(ticket.created_at, 'MMMM DD, YYYY h:mm A')} />
        <TicketInformationItem label="Updated" value={dateTime.format(ticket.updated_at, 'MMMM DD, YYYY h:mm A')} />
        <TicketInformationItem label="Source" value={sourceLabels[ticket.source] || '***UNKNOWN***'} />
        <TicketInformationItem label="Type" value={ticket.type || 'General'} />

        {/* Custom Fields */}
        {ticket.custom_fields && Object.keys(ticket.custom_fields).length > 0 && (
          <>
            {Object.entries(ticket.custom_fields).map(([key, value]) => {
              return (
                <TicketInformationItem key={key} label={capitalize(key.replace(/_/g, ' '))} value={String(value)} />
              );
            })}
          </>
        )}

        {/* Due Dates */}
        {(ticket.fr_due_by || ticket.due_by) && (
          <>
            {ticket.fr_due_by && (
              <TicketInformationItem label="First Response Due" value={formatDueDate(ticket.fr_due_by).formatted} />
            )}
            {ticket.due_by && (
              <TicketInformationItem label="Resolution Due" value={formatDueDate(ticket.due_by).formatted} />
            )}
          </>
        )}

        <Header
          link={ticket.requester?.id ? `/contacts/${ticket.requester?.id}` : undefined}
          imageUrl={ticket.requester?.profile_image?.[0]?.url ?? undefined}
          name={nameResolver(ticket.requester?.first_name, ticket.requester?.last_name, '***UNKNOWN***')}
          email={ticket?.requester?.emails?.[0]?.email || '***UNKNOWN***'}
        />
      </div>
    </div>
  );
}
